/**
 * Right-hand side panel for the submission status screen.
 *
 * Depending on the resolved display state it renders a "Steps to be Completed"
 * checklist and/or the {@link AgentStepsTracker} ("Steps Completed by Agent").
 * The in-progress state renders no side panel.
 *
 * @module submissionSidePanel
 */
import {
  AgentStep,
  AgentStepsTracker,
  SpinningLoader,
} from "@risalabs_frontend_org/oasis-ui-kit";
import { SubmissionDisplayState } from "../../utils/submissionDisplayState";
import {
  getSubmissionSidePanelData,
  SidePanelStep,
} from "../../utils/submissionSidePanelData";

/** Props for {@link SubmissionSidePanel}. */
interface SubmissionSidePanelProps {
  state: SubmissionDisplayState;
  agentSteps?: AgentStep[] | null;
  /** Payer-portal URL opened by the "Log in to payer portal" checklist link. */
  portalLoginUrl?: string;
  /** Whether the agent steps are still being fetched from the audit trail. */
  agentStepsLoading?: boolean;
}

/** Small external-link glyph shown next to a portal-link step. */
const PortalLinkIcon = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="#0056D6"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    className="shrink-0"
  >
    <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
    <polyline points="15 3 21 3 21 9" />
    <line x1="10" y1="14" x2="21" y2="3" />
  </svg>
);

/**
 * Renders the "Steps to be Completed" bordered card with a numbered checklist.
 *
 * Steps flagged with `hasPortalLink` render a clickable external-link glyph
 * only when a `portalLoginUrl` is available; when the URL is null the glyph is
 * hidden entirely so no dead link is shown.
 *
 * @input steps - The ordered list of follow-up steps to display.
 * @input portalLoginUrl - The payer-portal URL for portal-link steps, if any.
 * @returns The checklist card element.
 */
const StepsToBeCompletedCard = ({
  steps,
  portalLoginUrl,
}: {
  steps: SidePanelStep[];
  portalLoginUrl?: string;
}) => (
  <div className="submission-side-panel__steps flex flex-col items-start gap-4 rounded-md border border-primaryGray-15 bg-white px-5 pb-[1.5625rem] pt-4">
    <div className="font-['Lato'] text-small font-bold leading-small text-primaryGray-4">
      Steps to be Completed
    </div>
    <ol className="flex flex-col gap-3">
      {steps.map((step, index) => (
        <li
          key={step.id}
          className="flex items-start gap-1.5 font-['Lato'] text-tiny font-normal leading-tiny tracking-tightest text-primaryGray-1"
        >
          <span className="shrink-0 font-semibold">{index + 1}.</span>
          <span>{step.text}</span>
          {step.hasPortalLink && portalLoginUrl && (
            <a
              href={portalLoginUrl}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Open payer portal"
              className="inline-flex"
            >
              <PortalLinkIcon />
            </a>
          )}
        </li>
      ))}
    </ol>
  </div>
);

/**
 * Loading placeholder shown while the agent steps are fetched from the audit
 * trail, so the tracker area is not misleadingly empty during the request.
 *
 * @returns The bordered "Steps Completed by Agent" loading card.
 */
const AgentStepsLoadingCard = () => (
  <div className="submission-side-panel__agent-loading flex flex-col items-start gap-4 rounded-md border border-primaryGray-15 bg-white px-5 pb-[1.5625rem] pt-4">
    <div className="font-['Lato'] text-small font-bold leading-small text-primaryGray-4">
      Steps Completed by Agent
    </div>
    <div className="flex w-full flex-col items-center justify-center gap-2 py-6 [&_.spinning-loader]:!border-y-black">
      <SpinningLoader />
      <div className="font-['Lato'] text-tiny font-normal leading-tiny text-primaryGray-4">
        Loading agent steps...
      </div>
    </div>
  </div>
);

/**
 * Side panel container that picks the appropriate content for the display state.
 *
 * @input props - The resolved display state of the submission screen.
 * @returns The side panel, or null when the state has no side panel content.
 */
const SubmissionSidePanel = (props: SubmissionSidePanelProps) => {
  const { stepsToBeCompleted } = getSubmissionSidePanelData(props.state);
  const agentSteps = props.agentSteps;
  const hasAgentSteps = Boolean(agentSteps && agentSteps.length > 0);

  if (!stepsToBeCompleted && !hasAgentSteps && !props.agentStepsLoading) {
    return null;
  }

  return (
    <div className="submission-side-panel flex max-h-full w-[30%] shrink-0 flex-col gap-6 self-start">
      {stepsToBeCompleted && (
        <StepsToBeCompletedCard
          steps={stepsToBeCompleted}
          portalLoginUrl={props.portalLoginUrl}
        />
      )}
      {props.agentStepsLoading ? (
        <AgentStepsLoadingCard />
      ) : (
        hasAgentSteps && (
          <div className="min-h-0 flex-1 overflow-y-auto pr-1">
            <AgentStepsTracker
              title="Steps Completed by Agent"
              steps={agentSteps as AgentStep[]}
            />
          </div>
        )
      )}
    </div>
  );
};

export default SubmissionSidePanel;
